import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import {
  api,
  ApiError,
  ROLE_LABELS,
  type Invitation,
  type ListResponse,
  type Me,
  type Member,
  type LeaveBalance,
} from '../lib/api'
import { listItemVariants, listVariants, springSoft } from '../lib/motion'
import { Banner, Button, Card, Chip, Dialog, SelectField, Spinner, TextField, useToast } from '../ui'

export function TeamPage({ me }: { me: Me }) {
  const tenant = me.active_tenant
  const queryClient = useQueryClient()
  const toast = useToast()
  const [createOpen, setCreateOpen] = useState(false)
  /** Wen laden wir gerade ein? `null` = niemanden, sonst der Personaleintrag. */
  const [inviteFor, setInviteFor] = useState<Member | null | 'neu'>(null)
  const [editing, setEditing] = useState<Member | null>(null)

  const canManage = tenant?.role === 'owner' || tenant?.role === 'manager'

  const members = useQuery({
    queryKey: ['members', tenant?.id],
    queryFn: () => api.get<ListResponse<Member>>('/api/members'),
    enabled: Boolean(tenant),
  })

  const invitations = useQuery({
    queryKey: ['invitations', tenant?.id],
    queryFn: () => api.get<ListResponse<Invitation>>('/api/invitations'),
    enabled: Boolean(tenant) && canManage,
  })

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch(`/api/members/${id}/role`, { role }),
    onSuccess: () => {
      toast('Rolle geändert.', 'success')
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const removeMember = useMutation({
    mutationFn: (id: string) => api.delete(`/api/members/${id}`),
    onSuccess: () => {
      toast('Aus dem Betrieb entfernt.', 'success')
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const revokeInvitation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/invitations/${id}`),
    onSuccess: () => {
      toast('Einladung zurückgezogen.', 'success')
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (!tenant) return <Card title="Kein Betrieb ausgewählt">Wähle oben einen Betrieb.</Card>
  if (members.isPending) return <Spinner />

  const items = members.data?.items ?? []
  const offen = invitations.data?.items ?? []

  return (
    <div className="stack">
      <div className="row row--between">
        <h1>Team</h1>
        {canManage && (
          <span className="row">
            {/* Anlegen steht vorn: Es ist der übliche Weg. Eingeladen wird,
                wer die App auch benutzen soll — das sind selten alle. */}
            <Button onClick={() => setCreateOpen(true)}>Person anlegen</Button>
            <Button variant="outlined" onClick={() => setInviteFor('neu')}>
              Einladen
            </Button>
          </span>
        )}
      </div>

      {canManage && items.length <= 1 && (
        <Banner tone="info">
          Lege deine Belegschaft an — ganz ohne E-Mail-Adressen. Wer angelegt ist, lässt sich
          sofort verplanen. Ein Zugang zur App ist optional und jederzeit nachreichbar.
        </Banner>
      )}

      <Card>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                {canManage && <th aria-label="Aktionen" />}
              </tr>
            </thead>
            <motion.tbody variants={listVariants(items.length)} initial="hidden" animate="visible">
              {items.map((member) => (
                <motion.tr
                  key={member.membership_id}
                  variants={listItemVariants}
                  transition={springSoft()}
                >
                  <td data-label="Name">
                    {member.full_name}
                    {member.user_id === me.id && <span className="muted"> · du</span>}
                  </td>
                  <td data-label="E-Mail" className="muted">
                    {member.email ?? '—'}
                  </td>
                  <td data-label="Rolle">
                    {canManage && member.user_id !== me.id ? (
                      <select
                        className="shell__select"
                        value={member.role}
                        disabled={changeRole.isPending}
                        onChange={(event) =>
                          changeRole.mutate({
                            id: member.membership_id,
                            role: event.target.value,
                          })
                        }
                      >
                        {/* Was der Server nicht erlaubt, weist er ab — die
                            Auswahl hier ist Bequemlichkeit, keine Schranke.
                            „Inhaber" fehlt ohne Konto trotzdem: Diese Rolle
                            setzt eine Anmeldung voraus, und eine Auswahl
                            anzubieten, die verlässlich scheitert, ist keine
                            Bequemlichkeit. */}
                        {(tenant.role === 'owner'
                          ? member.has_account
                            ? ['owner', 'manager', 'employee']
                            : ['manager', 'employee']
                          : ['employee']
                        ).map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Chip tone={member.role === 'owner' ? 'primary' : 'neutral'}>
                        {ROLE_LABELS[member.role]}
                      </Chip>
                    )}
                  </td>
                  <td data-label="Status">
                    {/* Stillgelegt zuerst: Das ist die Angabe, die den Plan
                        betrifft. Ob jemand ein Konto hat, ist daneben
                        zweitrangig. */}
                    {!member.is_active && <Chip tone="warning">stillgelegt</Chip>}{' '}
                    {/* Vier Zustände, nicht zwei. „Wartet auf Bestätigung" für
                        jemanden ohne Konto wäre schlicht gelogen — der wartet
                        auf gar nichts, er soll sich vielleicht nie anmelden. */}
                    {!member.has_account ? (
                      member.invitation_pending ? (
                        <Chip tone="warning">eingeladen</Chip>
                      ) : (
                        <Chip tone="neutral">kein Zugang</Chip>
                      )
                    ) : member.email_verified ? (
                      <Chip tone="success">bestätigt</Chip>
                    ) : (
                      <Chip tone="warning">wartet auf Bestätigung</Chip>
                    )}
                  </td>
                  {canManage && (
                    <td data-label="Aktion">
                      <Button variant="text" small onClick={() => setEditing(member)}>
                        Bearbeiten
                      </Button>
                      {!member.has_account && !member.invitation_pending && (
                        <Button variant="text" small onClick={() => setInviteFor(member)}>
                          Zugang einrichten
                        </Button>
                      )}
                      {member.user_id !== me.id && (
                        <Button
                          variant="text"
                          small
                          busy={removeMember.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `${member.full_name} wirklich aus dem Betrieb entfernen? ` +
                                  'Vergangene Schichten und Zeiten bleiben erhalten.',
                              )
                            ) {
                              removeMember.mutate(member.membership_id)
                            }
                          }}
                        >
                          Entfernen
                        </Button>
                      )}
                    </td>
                  )}
                </motion.tr>
              ))}
            </motion.tbody>
          </table>
        </div>
      </Card>

      {canManage && offen.length > 0 && (
        <Card title="Offene Einladungen">
          <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {offen.map((invitation) => (
              <li key={invitation.id} className="row row--between">
                <span>
                  {invitation.email}
                  <span className="muted"> · {ROLE_LABELS[invitation.role]}</span>
                </span>
                <span className="row">
                  <span className="muted" style={{ fontSize: '0.8125rem' }}>
                    gültig bis{' '}
                    {new Date(invitation.expires_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </span>
                  <Button
                    variant="text"
                    small
                    onClick={() => revokeInvitation.mutate(invitation.id)}
                  >
                    Zurückziehen
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <CreateMemberDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        canAssignManager={tenant.role === 'owner'}
      />

      <EditMemberDialog
        member={editing}
        onClose={() => setEditing(null)}
        isSelf={editing?.user_id === me.id}
        isOwner={tenant.role === 'owner'}
      />

      <InviteDialog
        open={inviteFor !== null}
        onClose={() => setInviteFor(null)}
        canAssignAnyRole={tenant.role === 'owner'}
        member={inviteFor === 'neu' ? null : inviteFor}
      />
    </div>
  )
}

function CreateMemberDialog({
  open,
  onClose,
  canAssignManager,
}: {
  open: boolean
  onClose: () => void
  canAssignManager: boolean
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState('')
  const [role, setRole] = useState('employee')
  const [employeeNo, setEmployeeNo] = useState('')
  const [weeklyHours, setWeeklyHours] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/members', {
        full_name: name,
        role,
        employee_no: employeeNo.trim() || null,
        weekly_hours: weeklyHours.trim() || null,
      }),
    onSuccess: () => {
      toast(`${name} angelegt.`, 'success')
      queryClient.invalidateQueries({ queryKey: ['members'] })
      setName('')
      setEmployeeNo('')
      setWeeklyHours('')
      setError(null)
      onClose()
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog open={open} onClose={onClose} title="Person anlegen">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          create.mutate()
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        <TextField
          label="Name"
          required
          minLength={2}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Ab sofort verplanbar. Ein Zugang zur App ist nicht nötig."
        />
        <SelectField label="Rolle" value={role} onChange={(event) => setRole(event.target.value)}>
          {(canAssignManager ? ['employee', 'manager'] : ['employee']).map((value) => (
            <option key={value} value={value}>
              {ROLE_LABELS[value]}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Personalnummer"
          value={employeeNo}
          onChange={(event) => setEmployeeNo(event.target.value)}
          hint="Optional."
        />
        <TextField
          label="Wochenstunden laut Vertrag"
          type="number"
          min={0}
          max={80}
          step="0.25"
          value={weeklyHours}
          onChange={(event) => setWeeklyHours(event.target.value)}
          hint="Optional. Ohne Angabe prüft die Planung nur die gesetzliche Obergrenze."
        />
        <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
          Eine E-Mail-Adresse wird hier nicht gespeichert — sie wird erst gebraucht, wenn du
          später einen Zugang einrichtest.
        </p>
        <div className="row row--between">
          <Button variant="text" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button type="submit" busy={create.isPending}>
            Anlegen
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function InviteDialog({
  open,
  onClose,
  canAssignAnyRole,
  member,
}: {
  open: boolean
  onClose: () => void
  canAssignAnyRole: boolean
  /** Gesetzt, wenn der Zugang zu einer bereits angelegten Person gehört. */
  member: Member | null
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('employee')
  const [error, setError] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: () =>
      api.post('/api/invitations', {
        email,
        // Bei einer bestehenden Person gilt deren Rolle; der Server ignoriert
        // diese hier, damit die Einladung sie nicht still überschreibt.
        role: member ? member.role : role,
        membership_id: member?.membership_id ?? null,
      }),
    onSuccess: () => {
      toast(`Einladung an ${email} verschickt.`, 'success')
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
      setEmail('')
      setError(null)
      onClose()
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={member ? `Zugang für ${member.full_name}` : 'Person einladen'}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          invite.mutate()
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        {member && (
          <Banner tone="info">
            Der Zugang wird mit dem bestehenden Eintrag verbunden. Alles bereits Geplante bleibt
            erhalten.
          </Banner>
        )}
        <TextField
          label="E-Mail-Adresse"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          hint="Die Einladung gilt nur für genau diese Adresse."
        />
        {member ? (
          <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
            Rolle: {ROLE_LABELS[member.role]} — wie bisher hinterlegt.
          </p>
        ) : (
          <SelectField
            label="Rolle"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            hint={
              canAssignAnyRole
                ? undefined
                : 'Als Schichtleitung kannst du Mitarbeitende aufnehmen.'
            }
          >
            {(canAssignAnyRole ? ['employee', 'manager', 'owner'] : ['employee']).map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
          </SelectField>
        )}
        <div className="row row--between">
          <Button variant="text" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button type="submit" busy={invite.isPending}>
            Einladen
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * Stammdaten einer Person.
 *
 * Den **Namen** gibt es hier nur für Einträge ohne Konto. Wer ein Konto hat,
 * pflegt ihn im eigenen Profil — sonst gäbe es zwei Wahrheiten, und die
 * Leitung könnte den angezeigten Namen einer Person ändern, ohne dass diese es
 * merkt. Die **E-Mail-Adresse** fehlt aus demselben Grund, nur schärfer: Sie
 * ist die Anmeldeidentität, und wer sie ändern darf, kann das Konto übernehmen.
 */
function EditMemberDialog({
  member,
  onClose,
  isSelf,
  isOwner,
}: {
  member: Member | null
  onClose: () => void
  isSelf: boolean
  /** Stundenlohn ist Inhabersache — die Schichtleitung sieht das Feld nicht. */
  isOwner: boolean
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState('')
  const [employeeNo, setEmployeeNo] = useState('')
  const [weeklyHours, setWeeklyHours] = useState('')
  const [active, setActive] = useState(true)
  const [wage, setWage] = useState('')
  const [leaveDays, setLeaveDays] = useState('')
  const [carryover, setCarryover] = useState('')
  const [error, setError] = useState<string | null>(null)
  const jahr = new Date().getFullYear()

  // Beim Öffnen die gespeicherten Werte übernehmen. Ohne das zeigte der Dialog
  // beim zweiten Öffnen noch die Eingaben der vorigen Person.
  useEffect(() => {
    if (!member) return
    setName(member.full_name)
    setEmployeeNo(member.employee_no ?? '')
    setWeeklyHours(member.weekly_hours ?? '')
    setActive(member.is_active)
    setWage(member.hourly_wage ?? '')
    setError(null)
  }, [member])

  // Urlaubsstand nur für die geöffnete Person nachladen — die Liste aller
  // Stände zu holen wäre für einen Dialog verschwenderisch.
  const stand = useQuery({
    queryKey: ['leave-balances', jahr],
    queryFn: () => api.get<ListResponse<LeaveBalance>>(`/api/leave/balances?year=${jahr}`),
    enabled: member !== null,
  })
  const eigenerStand = stand.data?.items.find(
    (item) => item.membership_id === member?.membership_id,
  )

  useEffect(() => {
    if (!eigenerStand) return
    setLeaveDays(eigenerStand.entitled_days ?? '')
    setCarryover(eigenerStand.carryover_days ?? '')
  }, [eigenerStand])

  const saveLeave = useMutation({
    mutationFn: () =>
      api.put(`/api/members/${member!.membership_id}/leave`, {
        year: jahr,
        entitled_days: leaveDays.trim() || '0',
        carryover_days: carryover.trim() || '0',
      }),
    onSuccess: () => {
      toast('Urlaubsanspruch gespeichert.', 'success')
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] })
    },
    onError: (err) => setError((err as ApiError).message),
  })

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/members/${member!.membership_id}`, {
        // Nur mitschicken, was diese Rolle überhaupt setzen darf — der Server
        // weist es sonst ab, und der ganze Vorgang schlüge fehl.
        ...(isOwner ? { hourly_wage: wage.trim() || null } : {}),
        // Nur mitschicken, was die Leitung überhaupt ändern darf.
        ...(member!.has_account ? {} : { full_name: name }),
        // Leer heißt „entfernen" — als null, weil der Server zwischen
        // weggelassen und ausdrücklich geleert unterscheidet.
        employee_no: employeeNo.trim() || null,
        weekly_hours: weeklyHours.trim() || null,
        is_active: active,
      }),
    onSuccess: () => {
      toast('Gespeichert.', 'success')
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog
      open={member !== null}
      onClose={onClose}
      title={member ? `${member.full_name} bearbeiten` : 'Bearbeiten'}
    >
      {member && (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            save.mutate()
          }}
        >
          {error && <Banner tone="error">{error}</Banner>}

          {member.has_account ? (
            <>
              <TextField label="Name" value={member.full_name} readOnly disabled />
              <TextField label="E-Mail-Adresse" value={member.email ?? ''} readOnly disabled />
              <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
                Name und Adresse pflegt diese Person selbst im Profil. Die Adresse ist zugleich
                ihre Anmeldung — könnte die Leitung sie ändern, ließe sich damit jedes Konto
                übernehmen.
              </p>
            </>
          ) : (
            <TextField
              label="Name"
              required
              minLength={2}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}

          <div className="field-row">
            <TextField
              label="Personalnummer"
              value={employeeNo}
              onChange={(event) => setEmployeeNo(event.target.value)}
            />
            <TextField
              label="Wochenstunden"
              type="number"
              min={0}
              max={80}
              step="0.25"
              value={weeklyHours}
              onChange={(event) => setWeeklyHours(event.target.value)}
              hint="Leer = nur die gesetzliche Grenze gilt."
            />
          </div>

          {isOwner && (
            <TextField
              label="Stundenlohn (brutto)"
              type="number"
              min={0}
              max={999}
              step="0.01"
              value={wage}
              onChange={(event) => setWage(event.target.value)}
              hint="Grundlage der Personalkostenrechnung. Nur du als Inhaber siehst ihn."
            />
          )}

          <div className="field-row">
            <TextField
              label={`Urlaubsanspruch ${jahr} (Tage)`}
              type="number"
              min={0}
              max={400}
              step="0.5"
              value={leaveDays}
              onChange={(event) => setLeaveDays(event.target.value)}
            />
            <TextField
              label="Übertrag aus dem Vorjahr"
              type="number"
              min={0}
              max={400}
              step="0.5"
              value={carryover}
              onChange={(event) => setCarryover(event.target.value)}
              hint="Von Hand — ob Resturlaub verfällt, weiß die Software nicht."
            />
          </div>
          {eigenerStand && (
            <p className="muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
              Genommen: {eigenerStand.taken_days} Tage
              {eigenerStand.pending_days > 0 && ` · beantragt: ${eigenerStand.pending_days}`}
              {eigenerStand.remaining_days !== null && ` · Rest: ${eigenerStand.remaining_days}`}
            </p>
          )}
          <div className="row">
            <Button
              variant="outlined"
              small
              type="button"
              busy={saveLeave.isPending}
              onClick={() => saveLeave.mutate()}
            >
              Urlaubsanspruch speichern
            </Button>
          </div>

          {!isSelf && (
            <label className="row" style={{ gap: 'var(--sp-2)' }}>
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                style={{ width: 'auto', flex: '0 0 auto' }}
              />
              <span>
                Aktiv
                <span className="muted">
                  {' '}
                  — stillgelegte Personen lassen sich nicht mehr neu einteilen. Bereits geplante
                  Schichten bleiben bestehen.
                </span>
              </span>
            </label>
          )}

          <div className="row row--between">
            <Button variant="text" type="button" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" busy={save.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
